import Table from "@/src/components/atom/Table";
import SectionCardWithoutHeader from "@/src/components/molecule/SectionCardWithoutHeader";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import { purchaseRefundColumns } from "../columns/purchaseRefundColumns";
import { useTabContext } from "../context/creditContext";
import { CreditSelectTabState } from "..";
import useTableSort from "@/src/hooks/useTableSort";
import { createUnderLineLinkAction } from "@/src/utils/row-data-util";
import { authApi } from "@/src/store/apis/auth";
import { getAmountOfPayment } from "../../SubscriptionManagement/utils/getPayments";
import { openInvoiceInNewWindow } from "@/src/components/template/pdfs/Invoice/InvoiceContent";
import { commonApi } from "@/src/store/apis/common";
import { isUndefined } from "@/src/utils/is";
import {
  getFormattedTimezoneDateWithOffset,
  getTimezoneDateWithOffset,
} from "@/src/hooks/useCompanyLocalDate";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import { useLazyGetPaymentsQuery } from "@/src/store/apis/subscription";
import { Sort } from "types";
import useAlert from "@/src/hooks/useAlert";
import {
  PaymentDto,
  PaymentType,
} from "@/src/store/apis/subscription/interface";
import { VAT_RATE } from "./dialog/BuyCreditDialog";

const emptyArray: PaymentDto[] = [];

export const usePurchaseRefundQuery = () => {
  const [getPayments, { isFetching, currentData }] = useLazyGetPaymentsQuery({
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        isFetching,
        currentData: isStable ? currentData : { count: 0, rows: emptyArray },
      };
    },
  });

  return {
    fetchPurchaseHistory: async (params?: {
      page?: number;
      pageSize?: number;
      sort?: string;
      order?: Sort["order"];
    }) => {
      const creditParams = {
        page: params?.page || 1,
        pageSize: params?.pageSize || 10,
        sort: params?.sort || undefined,
        order: params?.order || undefined,
        paymentType: "CREDIT" as PaymentType,
      };

      await getPayments(creditParams).unwrap();
    },
    isFetching,
    payments: currentData?.rows || emptyArray,
    count: currentData?.count || 0,
  };
};

function PurchaseRefund() {
  const gridRef = useRef<AgGridReact>(null);
  const alert = useAlert();
  const { fetchPurchaseHistory, isFetching, payments, count } =
    usePurchaseRefundQuery();

  const [columnDefs] = useState<ColDef[]>(purchaseRefundColumns);
  const [isReady, setIsReady] = useState<boolean>(false);

  const tabContext = useTabContext(CreditSelectTabState.PURCHASE_REFUND);
  const { pagination, onPagination, sort, onSortChange, order, onOrderChange } =
    tabContext;

  const session = authApi.endpoints.getSession.useQueryState(undefined);
  const { countryList } =
    commonApi.endpoints.getCommonCodeViaCodeName.useQueryState(
      {
        codeName: "COUNTRY",
      },
      {
        selectFromResult: ({ currentData, isFetching, isError }) => {
          const isUnstable = isUndefined(currentData) || isError || isFetching;
          const isStable = !isUnstable;

          return {
            isFetching,
            countryList: isStable ? currentData : [],
          };
        },
      }
    );

  useTableSort({
    gridRef,
    sort,
    order,
    isReady,
  });

  useAgGridHeaderRefresh({
    gridRef: gridRef.current,
    isReady,
    headerSet: [
      {
        columnKey: "paymentNo",
        langKey: "table:purchaseNumber",
      },
      {
        columnKey: "purchaseCredit",
        langKey: "table:purchaseCredit",
      },
      {
        columnKey: "paymentAmount",
        langKey: "table:paymentAmount",
      },
      {
        columnKey: "createdAt",
        langKey: "table:paymentTime",
      },
      {
        columnKey: "status",
        langKey: "table:status",
      },
      {
        columnKey: "management",
        langKey: "table:management",
      },
    ],
  });

  const handlePaymentNoClick = async (payment: any) => {
    const price = getAmountOfPayment({
      exchangeRate: Number(payment?.exchangeRate),
      price: Number(payment.price),
      currencyUnit: payment.currencyUnit,
    });

    const qty = payment.paymentDetail.replace(/[^0-9]/g, "");
    const unitPrice = Math.round(price / (1 + VAT_RATE) / Number(qty));
    const tax = Math.round(price - price / (1 + VAT_RATE));

    openInvoiceInNewWindow(
      {
        title: "CREDIT PURCHASE INVOICE",
        invoiceColumnTitle: "PURCHASE INFORMATION",
        type: "purchase",
        invoiceNo: payment?.paymentNo || "-",
        invoiceDate:
          getFormattedTimezoneDateWithOffset({
            date: payment?.createdAt,
            gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
          }) || "-",
        buyer: payment?.exporter?.companyName || "-",
        buyerAddress: `${[
          session.currentData?.row?.exporter.postalCode || "",
          session.currentData?.row?.exporter.streetAddress || "",
          session.currentData?.row?.exporter.locality || "",
          session.currentData?.row?.exporter.region || "",
        ]
          .filter(Boolean)
          .join(", ")}`,
        buyerContact: `${session.currentData?.row?.exporter.telPrefix}) ${session.currentData?.row?.exporter.tel}`,
        buyerCountry:
          countryList
            .find(
              (item) =>
                item.codeItemName ===
                session.currentData?.row.exporter.countryCodeItemName
            )
            ?.codeItemNameEn.toUpperCase() || "-",
        paymentDate: getFormattedTimezoneDateWithOffset({
          date: payment.createdAt,
          gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
          format: DATE_FORMAT_STRINGS.YYYY_MM_DD_HH_mm,
        }),
        currencyUnit: payment.currencyUnit as string,
        items: [
          {
            description: "Export Credit",
            unitPrice,
            tax,
            amount: price,
            qty,
          },
        ],
        period: `
      ${getFormattedTimezoneDateWithOffset({
        date: payment.createdAt,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
      })}–${getTimezoneDateWithOffset({
        date: payment.createdAt,
        gmtOffset: session.currentData?.row.exporter.gmtOffset || 0,
      })
        .add(5, "year")
        .subtract(1, "day")
        .format(DATE_FORMAT_STRINGS.YYYY_MM_DD)}
      `,
        total: {
          unitPrice,
          tax,
          totalPrice: price,
        },
      },
      `invoice-${payment?.paymentNo}.pdf`
    );
  };

  // Submit
  const handleSubmit = useCallback(
    async (param?: {
      page?: number;
      pageSize?: number;
      sort?: string;
      order?: string;
    }) => {
      const page = param?.page || pagination.page;
      const pageSize = param?.pageSize || pagination.pageSize;
      const tableSort = param?.sort || sort;
      const tableOrder = (param?.order || order) as Sort["order"];

      const creditParams = {
        page,
        pageSize,
        sort: tableSort,
        order: tableOrder,
        paymentType: "CREDIT" as PaymentType,
      };

      try {
        await fetchPurchaseHistory(creditParams);
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;

        if (e.data.statusCode !== 404) {
          alert.showAlert({ type: "error", message });
        }
      }
    },
    [
      alert,
      fetchPurchaseHistory,
      order,
      pagination.page,
      pagination.pageSize,
      sort,
    ]
  );

  const handlePaginationClick = (page: number, pageSize: number) => {
    onPagination({ page, pageSize });
    handleSubmit({
      page,
      pageSize,
    });
  };

  // Loading
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef?.current?.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  useEffect(() => {
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SectionCardWithoutHeader>
      <Table
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={payments}
        totalPage={count}
        onGridReady={() => setIsReady(true)}
        handlePaginationClick={handlePaginationClick}
        pageSize={pagination.pageSize}
        page={pagination.page}
        onSortChange={(sortSource, isClickedHeader) => {
          if (isClickedHeader) {
            if (!sortSource) {
              onSortChange(undefined);
              onOrderChange(undefined);
              handleSubmit({
                sort: undefined,
                order: undefined,
              });
            }

            handleSubmit({
              sort: (sortSource?.sort as string) || undefined,
              order:
                (sortSource?.order?.toUpperCase() as "ASC" | "DESC") ||
                undefined,
            });
          }
        }}
        components={{
          ...createUnderLineLinkAction(handlePaymentNoClick, "paymentNo"),
        }}
      />
    </SectionCardWithoutHeader>
  );
}

export default PurchaseRefund;
